import { ENTIRE_COLLECTION_REPLACED, targetDocumentCollections, } from "@buildwithflux/models";
import { chain, merge } from "lodash";
import mapValues from "lodash/mapValues";
import pick from "lodash/pick";
import { getImmerPatches } from "../immerPatchSequence";
/**
 * Functions for extracting changes from patches.
 * TODO: I think there's a way to unify these two functions but it's not obvious yet.
 */
// Automatically stays in sync with the declaration of the keys that we inspect.
function keyIsValidCollectionTarget(k) {
    for (const collectionName of targetDocumentCollections) {
        if (collectionName == k) {
            return true;
        }
    }
    return false;
}
/**
 * Extracts all of the changes _at the uid level_ from a given document patch.  This implements a form of path
 * compression, where we treat an object as having been changed if any of its subpaths have changed, or the
 * whole collection has been replaced
 *
 * @param excludePath matches on the path of each immer patch, segment by segment. A
 * '*' matches all in a segment. Example:
 *  ["pcbLayoutNodes", "*", "childUids"]
 */
export function getCollectionChangesFromPatch(patch, direction = "forward", excludePath) {
    const result = {};
    const patches = getImmerPatches(patch);
    for (const patch of patches[direction].flat()) {
        const changedCollection = patch.path[0];
        // NOTE: Some actions could end up replacing the whole collection and so there so no changedObjectUid.
        // One typical example is in `updateWiring` when adding the first route in schematic
        // editor. In this case we still wanna record it as a collection change
        const changedObjectUid = patch.path[1] || ENTIRE_COLLECTION_REPLACED;
        // The criteria here: the changed path is one of our collections of interest, and that the changed path
        // contains a uid (or at least, we presume here that it's a uid).
        if (keyIsValidCollectionTarget(changedCollection) && changedObjectUid) {
            if (excludePath && isExcluded(excludePath, patch)) {
                continue;
            }
            // TODO: It might be possible to do this in a slightly prettier way.  However my attempts to do it a pretty
            // way weren't successful at preserving type safety, so ugly it is for now.
            const changesSoFar = result[changedCollection];
            // If the path stops at the 2nd element, it means we just have an element uid.  If it goes deeper, we want
            // to record that the object was changed without respect to its sub-parts.
            const changeOp = patch.path[2] === undefined ? patch.op : "replace";
            if (changesSoFar) {
                changesSoFar[changedObjectUid] = changeOp;
            }
            else {
                result[changedCollection] = { [changedObjectUid]: changeOp };
            }
        }
    }
    return result;
}
export function isExcluded(excludePath, patch) {
    return excludePath.every((segment, index) => segment === patch.path[index] || segment === "*");
}
/**
 * Extracts all change values from a given document patch, converting the path to an object structure.
 */
export function getCollectionChangeValuesFromPatch(patch, direction = "forward") {
    let result = {};
    const patches = getImmerPatches(patch);
    for (const patch of patches[direction]) {
        if (!keyIsValidCollectionTarget(patch.path[0]))
            continue;
        const changeValue = patch.path.reduceRight((acc, key, idx) => (idx === patch.path.length - 1 ? { [key]: patch.value } : { [key]: acc }), {});
        result = merge(result, changeValue);
    }
    return result;
}
/**
 * Extracts all of the changes _at the uid level_ from a given document patch, but only for those changes that match
 * a target collection and (optionally) a field on the type of objects in that collection.  For example, you can use
 * this function to ask the question "which pcbLayoutNodes had their pcbLayoutRuleSet changed in this patch?"
 */
export function getMatchingCollectionChangesFromPatch(patch, targetCollection, targetField, direction = "forward") {
    if (targetField) {
        const result = {};
        const patches = getImmerPatches(patch);
        for (const patch of patches[direction].flat()) {
            const changedCollection = patch.path[0];
            const changedObjectUid = patch.path[1];
            const changedObjectField = patch.path[2];
            const changedObjectSubfield = patch.path[3];
            // The criteria here: the changed path is one of our collections of interest, and that the changed path
            // contains a uid (or at least, we presume here that it's a uid).
            if (keyIsValidCollectionTarget(changedCollection) &&
                changedObjectUid &&
                changedCollection == targetCollection &&
                changedObjectField == targetField) {
                const changesSoFar = result[changedCollection];
                // If the path stops at the 2nd element, it means we just have an element uid.  If it goes deeper, we want
                // to record that the object was changed without respect to its sub-parts.
                const changeOp = changedObjectSubfield === undefined ? patch.op : "replace";
                if (changesSoFar) {
                    changesSoFar[changedObjectUid] = changeOp;
                }
                else {
                    result[changedCollection] = { [changedObjectUid]: changeOp };
                }
            }
        }
        return result;
    }
    else {
        const all = getCollectionChangesFromPatch(patch, direction);
        return pick(all, targetCollection);
    }
}
/**
 * Extracts all change values from a given document patch, converting the path to an object structure, but only for
 * those changes that match a target collection and (optionally) a field on the type of objects in that collection.
 */
export function getMatchingCollectionChangeValuesFromPatch(patch, targetCollection, targetField, direction = "forward") {
    if (!targetField) {
        const all = getCollectionChangeValuesFromPatch(patch, direction);
        return pick(all, targetCollection);
    }
    let result = {};
    const patches = getImmerPatches(patch);
    for (const patch of patches[direction]) {
        const changedCollection = patch.path[0];
        const changedObjectField = patch.path[2];
        if (!keyIsValidCollectionTarget(patch.path[0]) ||
            changedCollection !== targetCollection ||
            changedObjectField !== targetField) {
            continue;
        }
        const changeValue = patch.path.reduceRight((acc, key, idx) => (idx === patch.path.length - 1 ? { [key]: patch.value } : { [key]: acc }), {});
        result = merge(result, changeValue);
    }
    return result;
}
/**
 * Groups the output of getCollectionChangesFromPatch by the operation type:
 * add, replace or remove.
 *
 * @see getCollectionChangesFromPatch
 */
export function getCollectionChangesFromPatchByOp(patch, direction = "forward", excludePath) {
    const changedObjects = getCollectionChangesFromPatch(patch, direction, excludePath);
    return mapValues(changedObjects, (collectionChanges) => chain(collectionChanges)
        .entries()
        .groupBy(([_uid, op]) => op)
        .mapValues((group) => group.map(([uid]) => uid))
        // Lodash does not type the keys returned by groupBy
        // See https://stackoverflow.com/a/57616097/200312
        .value());
}
//# sourceMappingURL=inspect.js.map